// Where camera frames come from. Always the real camera — except in the mock
// and design builds with ?camfeed=demo, where a scripted fake feed stands in
// so the live camera can be exercised in desktop Chrome and Playwright. The
// fake feed is a dynamic import behind a build-time MODE check, so a
// production bundle drops it entirely (verified by grepping dist/).

import type { CameraDevice } from "./lens"

export interface StreamSource {
  name: "camera" | "fake"
  open(constraints: MediaStreamConstraints): Promise<MediaStream>
  // The cameras there are (names only once the camera is allowed).
  devices(): Promise<CameraDevice[]>
}

export const userMediaSource: StreamSource = {
  name: "camera",
  open: (constraints) => navigator.mediaDevices.getUserMedia(constraints),
  devices: async () => (await navigator.mediaDevices.enumerateDevices?.()) ?? [],
}

export async function resolveStreamSource(search: URLSearchParams): Promise<StreamSource> {
  if (
    (import.meta.env.MODE === "mock" || import.meta.env.MODE === "design") &&
    search.get("camfeed") === "demo"
  ) {
    const { fakeFeedSource } = await import("../dev/fake-camera-feed")
    return fakeFeedSource(search)
  }
  return userMediaSource
}

// Dev-only on-screen numbers (?camdebug=1), same gate as the fake feed.
export function cameraDebugEnabled(search: URLSearchParams): boolean {
  return (
    (import.meta.env.MODE === "mock" || import.meta.env.MODE === "design") &&
    search.get("camdebug") === "1"
  )
}

// Dev-only: the real-walk recorder on the phone test link (?camrec=1).
export function walkRecorderEnabled(search: URLSearchParams): boolean {
  return (
    (import.meta.env.MODE === "mock" || import.meta.env.MODE === "design") &&
    search.get("camrec") === "1"
  )
}

// Dev-only: run the camera's vision on the page instead of in a worker
// (?camvision=inline), to test the fallback.
export function visionInline(search: URLSearchParams): boolean {
  return (import.meta.env.MODE === "mock" || import.meta.env.MODE === "design") && search.get("camvision") === "inline"
}

// Dev-only: force a camera mode for demos and tests (?capture=native|guided|smart).
export function captureModeOverride(search: URLSearchParams): "smart" | "guided" | "native" | null {
  if (import.meta.env.MODE !== "mock" && import.meta.env.MODE !== "design") return null
  const value = search.get("capture")
  return value === "smart" || value === "guided" || value === "native" ? value : null
}
